import clsx from "clsx";
import {
  cloneElement,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from "react";

const fieldBase =
  "block w-full rounded-md border border-slate-500 bg-white px-3 py-2 text-sm text-ink-primary placeholder:text-ink-secondary focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue disabled:bg-slate-50 disabled:text-ink-secondary";

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  required?: boolean;
  children: ReactNode;
}

export const Field = ({ label, hint, error, className, required, children }: FieldProps) => {
  const uid = useId();
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const a11yProps: Record<string, unknown> = {};
  if (describedBy) a11yProps["aria-describedby"] = describedBy;
  if (error) a11yProps["aria-invalid"] = true;

  // Extraer el valor para la validación visual de extracción
  let value: unknown = undefined;
  if (isValidElement<{ value?: unknown; className?: string }>(children)) {
    value = children.props.value;
  }
  const isFilled = value !== undefined && value !== null && String(value).trim() !== "";

  const control = isValidElement<{ className?: string }>(children)
    ? cloneElement(children, {
        ...a11yProps,
        className: clsx(
          children.props.className,
          value !== undefined && (isFilled ? '!border-green-500 focus:!border-green-600 focus:!ring-green-600 !border-4' : '!border-accent-red focus:!border-accent-red focus:!ring-accent-red !border-4')
        )
      })
    : children;

  // Mostrar los iconos solo si la vista lo requiere (se asume que los campos de formulario de facturas los usan)
  // Como esto se aplica globalmente, añadiremos el check a todos los labels de los campos.

  return (
    <label className={clsx("flex flex-col gap-1 text-sm", className)}>
      {label ? (
        <span className="font-medium text-ink-secondary flex items-center">
          {label}
          {required ? <span className="ml-1 text-accent-red" aria-hidden="true">*</span> : null}
          {value !== undefined && (
            isFilled ? (
              <span className="text-green-500 ml-auto text-xs" title="Dato extraído">✅</span>
            ) : (
              <span className="text-accent-red ml-auto text-xs font-bold" title="Dato vacío">❌</span>
            )
          )}
        </span>
      ) : null}
      {control}
      {hint && !error ? (
        <span id={hintId} className="text-xs text-ink-secondary">{hint}</span>
      ) : null}
      {error ? (
        <span id={errorId} role="alert" className="text-xs text-accent-red">{error}</span>
      ) : null}
    </label>
  );
};

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = ({ invalid, className, ...rest }: InputProps) => (
  <input
    aria-invalid={invalid || undefined}
    {...rest}
    className={clsx(fieldBase, invalid && "border-accent-red focus:border-accent-red focus:ring-accent-red", className)}
  />
);

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = ({ invalid, className, children, ...rest }: SelectProps) => (
  <select
    aria-invalid={invalid || undefined}
    {...rest}
    className={clsx(fieldBase, invalid && "border-accent-red focus:border-accent-red focus:ring-accent-red", className)}
  >
    {children}
  </select>
);

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = ({ invalid, className, ...rest }: TextareaProps) => (
  <textarea
    aria-invalid={invalid || undefined}
    {...rest}
    className={clsx(fieldBase, "min-h-[80px]", invalid && "border-accent-red focus:border-accent-red focus:ring-accent-red", className)}
  />
);
